// @ts-check
// Momentos de recompensa de los editores (rediseño, Fase 6b; REDISENO-WEB.md,
// Fase 6, y plan de la Fase 3, §3). Solo con señales que YA existen, sin
// datos ni lógica nuevos, y sin tocar los paneles que no se tocan (el editor
// de diagramas es zona protegida; los paneles de intento, «no se toca»):
//
// - La consola de estado TECLEA su texto nuevo, rápido. El texto se escribe de
//   golpe, como siempre (el lector de pantalla lo lee una vez); lo que se
//   anima es un recorte que se abre de izquierda a derecha.
// - Si la ejecución fue bien (`data-resultado="exito"`, que ponen las
//   consolas de pseudocódigo y Python), la línea SE ENCIENDE al acabar de
//   escribirse.
// - En «Resultado de la corrección», con la solución correcta, los XP de
//   «Puntuación obtenida en este intento: N XP» CUENTAN hasta N (el CSS, con
//   la cifra envuelta aquí). La cabecera que se ilumina es solo CSS
//   (`momentos.css`).
//
// Todo se queda quieto con movimiento reducido y en clásico y plano
// (`--movimiento: 0`): se ve el estado final. Nunca retrasa el foco.
import { createSafeElement } from '../../security/safe-dom.js';
import { PREFIJO_PUNTUACION } from '../../utils/mensaje-puntuacion.js';
import { hayMovimiento, ritmoDelEstilo } from './movimiento.js';
import { EVENTO_DIALOGO_ABIERTO } from './eventos.js';
import './momentos.css';

// Vive en `movimiento.js` (lo usa también el panel del profesor).
export { hayMovimiento };

/**
 * Las líneas de estado de las tres consolas. Viven en módulos que no se
 * tocan: son copias, atadas por `momentos.spec.js` a su origen.
 */
export const CONSOLAS_DE_ESTADO =
  '.canvas-status, .execution-panel-status, .pseudo-run-console-status, .execution-console-status';

/**
 * El veredicto de «Resultado de la corrección» en cada editor (lleva
 * `data-correcto`). Copias, atadas como las de arriba; las usa también
 * `momentos.css`.
 */
export const VEREDICTOS = /** @type {const} */ ([
  'autocorrection-panel-verdict',
  'pseudo-attempt-panel-verdict',
  'python-attempt-panel-verdict',
]);

/** Milisegundos por letra al teclear, y el tope: «rápido». */
const MS_POR_LETRA = 10;
const TECLEO_MAXIMO = 360;

/** Lo que dura el brillo de una línea que se enciende. */
const ENCENDIDO = 700;

/**
 * Cuánto se tarda en teclear un texto.
 * @param {string} texto
 */
function duracionDeTecleo(texto) {
  return Math.min(texto.length * MS_POR_LETRA, TECLEO_MAXIMO);
}

/**
 * Píxeles que quedan entre el final del texto y el borde derecho de la línea:
 * el recorte se abre hasta ahí, y no tecleando aire.
 * @param {HTMLElement} elemento
 */
function hueco(elemento) {
  try {
    const rango = document.createRange();
    rango.selectNodeContents(elemento);
    const texto = rango.getBoundingClientRect();
    const caja = elemento.getBoundingClientRect();
    return Math.max(0, Math.round(caja.right - texto.right));
  } catch {
    return 0;
  }
}

/**
 * Teclea el texto de la línea: un recorte que se abre de izquierda a derecha,
 * a saltos de letra en Arcade (su ritmo es `steps()`) y con el ritmo de su
 * estilo en los otros dos.
 * @param {HTMLElement} elemento
 * @returns {Animation | null}
 */
export function teclear(elemento) {
  const texto = elemento.textContent ?? '';
  if (texto === '' || !hayMovimiento() || typeof elemento.animate !== 'function') return null;
  const ritmo = ritmoDelEstilo();
  const easing = ritmo.startsWith('steps(')
    ? `steps(${texto.length}, jump-end)`
    : ritmo || 'ease-out';
  return elemento.animate(
    [{ clipPath: 'inset(0 100% 0 0)' }, { clipPath: `inset(0 ${hueco(elemento)}px 0 0)` }],
    { id: 'momento-teclear', duration: duracionDeTecleo(texto), easing },
  );
}

/**
 * La línea se enciende: un brillo del color de acierto que se apaga.
 * @param {HTMLElement} elemento
 * @param {number} [retraso] - Milisegundos antes de empezar (lo que tarda en
 *   teclearse).
 * @returns {Animation | null}
 */
export function encender(elemento, retraso = 0) {
  if (!hayMovimiento() || typeof elemento.animate !== 'function') return null;
  return elemento.animate(
    [{ textShadow: '0 0 0.6em var(--color-text-success)' }, { textShadow: '0 0 0 transparent' }],
    { id: 'momento-encender', duration: ENCENDIDO, delay: retraso, easing: 'ease-out' },
  );
}

/**
 * Vigila las líneas de estado de las consolas dentro de `raiz` y teclea las
 * que cambian de texto. El lienzo de diagramas se DESTRUYE y se reconstruye
 * en cada cambio del programa, con el mensaje vigente: por eso se recuerda el
 * último texto de cada consola, no de cada elemento.
 * @param {ParentNode} raiz
 * @param {string} [selector]
 * @returns {() => void} Deja de vigilar.
 */
export function vigilarConsolas(raiz, selector = CONSOLAS_DE_ESTADO) {
  const partes = selector.split(',').map((parte) => parte.trim());
  /** @type {Map<string, string>} */
  const vistos = new Map();
  /** @param {Element} elemento */
  const clave = (elemento) => partes.find((parte) => elemento.matches(parte)) ?? '';

  for (const elemento of raiz.querySelectorAll(selector)) {
    vistos.set(clave(elemento), elemento.textContent ?? '');
  }

  /** @param {HTMLElement} elemento */
  const revisar = (elemento) => {
    const texto = elemento.textContent ?? '';
    const k = clave(elemento);
    if (vistos.get(k) === texto) return;
    vistos.set(k, texto);
    const tecleo = teclear(elemento);
    if (elemento.dataset.resultado === 'exito') {
      encender(elemento, tecleo ? duracionDeTecleo(texto) : 0);
    }
  };

  const observador = new globalThis.MutationObserver((registros) => {
    /** @type {Set<HTMLElement>} */
    const tocadas = new Set();
    for (const registro of registros) {
      const nodo =
        registro.target instanceof globalThis.Element
          ? registro.target
          : registro.target.parentElement;
      const dentro = nodo?.closest(selector);
      if (dentro instanceof HTMLElement) tocadas.add(dentro);
      for (const nuevo of registro.addedNodes) {
        if (!(nuevo instanceof globalThis.Element)) continue;
        if (nuevo instanceof HTMLElement && nuevo.matches(selector)) tocadas.add(nuevo);
        for (const hija of nuevo.querySelectorAll(selector)) {
          if (hija instanceof HTMLElement) tocadas.add(hija);
        }
      }
    }
    for (const elemento of tocadas) if (elemento.isConnected) revisar(elemento);
  });
  observador.observe(raiz, { childList: true, characterData: true, subtree: true });
  return () => observador.disconnect();
}

/**
 * En un diálogo con la solución CORRECTA, envuelve la cifra de «Puntuación
 * obtenida en este intento: N XP» para que el CSS la haga contar hasta N. El
 * texto no cambia: la cifra de verdad sigue ahí para el lector de pantalla
 * (visualmente oculta) y la que cuenta es decorativa.
 * @param {Element} dialogo
 * @returns {boolean} Si ha envuelto alguna cifra.
 */
export function marcarPuntuacion(dialogo) {
  const acierto = VEREDICTOS.some((clase) =>
    dialogo.querySelector(`.${clase}[data-correcto='true']`),
  );
  if (!acierto) return false;
  let marcada = false;
  for (const parrafo of dialogo.querySelectorAll('p')) {
    const texto = parrafo.textContent ?? '';
    if (!texto.startsWith(PREFIJO_PUNTUACION) || parrafo.querySelector('.momento-xp')) continue;
    const partes = /^(\d+)( XP[\s\S]*)$/.exec(texto.slice(PREFIJO_PUNTUACION.length));
    if (!partes) continue;
    const [, cifra, resto] = partes;
    const envoltorio = createSafeElement('span', { className: 'momento-xp' });
    envoltorio.style.setProperty('--xp', cifra);
    envoltorio.style.setProperty('--xp-cifras', String(cifra.length));
    const vista = createSafeElement('span', { className: 'momento-xp-visto' });
    vista.setAttribute('aria-hidden', 'true');
    envoltorio.append(
      createSafeElement('span', { className: 'u-visually-hidden', text: cifra }),
      vista,
    );
    parrafo.replaceChildren(
      document.createTextNode(PREFIJO_PUNTUACION),
      envoltorio,
      document.createTextNode(resto),
    );
    marcada = true;
  }
  return marcada;
}

let activaciones = 0;
/** @type {(() => void) | null} */
let soltarActivos = null;

/**
 * Activa los momentos de un editor: los XP al abrirse un diálogo
 * (`ui-dialogo-abierto`, de `modal-dialog.js`) y las consolas de toda la
 * página. Una sola vez aunque se llame varias.
 * @returns {() => void} Los desactiva (cuando ya nadie los quiere).
 */
export function activarMomentos() {
  if (activaciones === 0 && globalThis.document?.body) {
    /** @param {Event} evento */
    const alAbrir = (evento) => {
      if (evento.target instanceof globalThis.Element) marcarPuntuacion(evento.target);
    };
    document.addEventListener(EVENTO_DIALOGO_ABIERTO, alAbrir);
    const soltarConsolas = vigilarConsolas(document.body);
    soltarActivos = () => {
      document.removeEventListener(EVENTO_DIALOGO_ABIERTO, alAbrir);
      soltarConsolas();
    };
  }
  activaciones += 1;
  let soltada = false;
  return () => {
    if (soltada) return;
    soltada = true;
    activaciones -= 1;
    if (activaciones === 0) {
      soltarActivos?.();
      soltarActivos = null;
    }
  };
}
